// utils/audioPlayer/context.ts — AudioContext singleton + buffer + priming.
// Dipakai orderSounds.ts (cash/ding) dan lpClick.ts (beep). SATU-SATUNYA modul
// yang boleh membuat AudioContext (1 singleton per tab) supaya resume/priming
// dari gesture mana pun berlaku untuk semua suara.

// Audio Context singleton
let audioContext: AudioContext | null = null;

export async function getAudioContext(): Promise<AudioContext> {
  if (!audioContext) {
    audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
  }
  if (audioContext.state === 'suspended') {
    await audioContext.resume();
  }
  return audioContext;
}

export async function loadBuffer(path: string, cache: { buffer: AudioBuffer | null }): Promise<AudioBuffer | null> {
  if (cache.buffer) return cache.buffer;
  try {
    const ctx = await getAudioContext();
    const response = await fetch(path);
    const arrayBuffer = await response.arrayBuffer();
    cache.buffer = await ctx.decodeAudioData(arrayBuffer);
    return cache.buffer;
  } catch (error) {
    console.error(`Error loading sound ${path}:`, error);
    return null;
  }
}

/** Buka kunci AudioContext pada gesture pertama (kebijakan autoplay browser). */
export async function primeAudio(): Promise<void> {
  try {
    const before = peekAudioState();
    await getAudioContext();
    // Beri tahu indikator footer hanya saat transisi terkunci → siap.
    if (before !== 'running' && peekAudioState() === 'running') {
      const { notifyHealthChanged } = await import('../realtimeHealth.js');
      notifyHealthChanged();
    }
  } catch { /* abaikan */ }
}

/** Status AudioContext tanpa membuatnya (null = belum pernah dipakai). */
export function peekAudioState(): AudioContextState | null {
  try {
    return audioContext ? audioContext.state : null;
  } catch {
    return null;
  }
}

export async function playBeepFallback(volume: number): Promise<void> {
  const ctx = await getAudioContext();
  if (ctx.state !== 'running') {
    // Tanpa gesture dulu browser menahan AudioContext di 'suspended' → beep
    // dijadwalkan tapi sunyi. Warn sekali supaya ketahuan di console.
    console.warn(
      '[audio] AudioContext masih suspended — klik di mana saja dulu, lalu Tes Suara.'
    );
  }
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  const t0 = ctx.currentTime;
  osc.type = 'sine';
  osc.frequency.setValueAtTime(880, t0);
  osc.frequency.setValueAtTime(1240, t0 + 0.09);
  gain.gain.setValueAtTime(0.0001, t0);
  gain.gain.exponentialRampToValueAtTime(Math.max(0.02, volume), t0 + 0.015);
  gain.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.24);
  osc.connect(gain);
  gain.connect(ctx.destination);
  osc.start(t0);
  osc.stop(t0 + 0.26);
}

export async function preloadSounds(): Promise<void> {
  try {
    const ctx = await getAudioContext();
    const oscillator = ctx.createOscillator();
    const gainNode = ctx.createGain();
    gainNode.gain.setValueAtTime(0, ctx.currentTime);
    oscillator.connect(gainNode);
    gainNode.connect(ctx.destination);
    oscillator.start();
    oscillator.stop(ctx.currentTime + 0.001);
  } catch (error) {
    console.error('Error preloading sounds:', error);
  }
}

export function isSoundSupported(): boolean {
  try {
    return !!(window.AudioContext || (window as any).webkitAudioContext);
  } catch {
    return false;
  }
}
